import { useRef, useState } from 'react';
import { analyzeOnboardingCoi } from '../api';
import { FileUpload } from '../components/FileUpload';
import type { FormData } from '../types';

interface Props {
  data: FormData;
  onChange: (updates: Partial<FormData>) => void;
  errors?: Record<string, string>;
}

const DATES = [
  { key: 'glExpiresAt', label: 'General Liability' },
  { key: 'autoExpiresAt', label: 'Automobile Liability' },
  { key: 'wcExpiresAt', label: 'Workers Compensation' },
  { key: 'umbrellaExpiresAt', label: 'Umbrella / Excess' },
] as const;

const LIMITS = [
  { key: 'glEachOccurrence', label: 'GL each occurrence' },
  { key: 'glAggregate', label: 'GL general aggregate' },
  { key: 'autoCombinedSingle', label: 'Auto combined single limit' },
  { key: 'wcEmployersLiability', label: "Employer's liability, each accident" },
  { key: 'umbrellaEachOccurrence', label: 'Umbrella each occurrence' },
] as const;

function displayDate(value: string): string {
  const [year, month, day] = value.split('-');
  return year && month && day ? `${month}/${day}/${year}` : value;
}

function displayMoney(value: string): string {
  return `$${Number(value).toLocaleString('en-US')}`;
}

export function InsuranceStep({ data, onChange, errors = {} }: Props) {
  const [busy, setBusy] = useState(false);
  const [analysisError, setAnalysisError] = useState('');
  const requestSequence = useRef(0);

  const parserUnavailable = data.coiParserStatus === 'failed';
  const missingFields =
    data.coiParserStatus && !parserUnavailable
      ? [
          ...DATES.filter((field) => !data[field.key]).map((field) => field.label),
          ...LIMITS.filter((field) => !data.coiLimits[field.key]).map((field) => field.label),
        ]
      : [];
  const canConfirm = !!data.coiAnalysisToken && (parserUnavailable || missingFields.length === 0);

  async function selectCertificate(coiFile: File | null) {
    const sequence = ++requestSequence.current;
    setAnalysisError('');
    onChange({
      coiFile,
      coiAnalysisToken: '',
      coiParserStatus: '',
      coiLimits: {},
      coiConfirmed: false,
      glExpiresAt: '',
      autoExpiresAt: '',
      wcExpiresAt: '',
      umbrellaExpiresAt: '',
    });
    if (!coiFile) return;

    setBusy(true);
    try {
      const analysis = await analyzeOnboardingCoi(data.contactEmail, coiFile);
      if (sequence !== requestSequence.current) return;
      const confidentlyReadLimits = Object.fromEntries(
        Object.entries(analysis.limits).filter(
          ([key, value]) => !!value && (analysis.limitConfidence[key] ?? 0) >= 1,
        ),
      );
      onChange({
        coiFile,
        coiAnalysisToken: analysis.analysisToken,
        coiParserStatus: analysis.parserStatus,
        coiLimits: confidentlyReadLimits,
        coiConfirmed: false,
        glExpiresAt: analysis.dates.gl?.slice(0, 10) ?? '',
        autoExpiresAt: analysis.dates.auto?.slice(0, 10) ?? '',
        wcExpiresAt: analysis.dates.wc?.slice(0, 10) ?? '',
        umbrellaExpiresAt: analysis.dates.umbrella?.slice(0, 10) ?? '',
      });
    } catch (error) {
      if (sequence === requestSequence.current) {
        setAnalysisError(error instanceof Error ? error.message : 'Certificate upload failed.');
      }
    } finally {
      if (sequence === requestSequence.current) setBusy(false);
    }
  }

  return (
    <div>
      <div className="step-header">
        <h2>Insurance (COI)</h2>
        <p>
          Upload your certificate and we will read its coverage dates and limits for you to
          confirm. You will not need to type values from the document.
        </p>
      </div>

      <div className="info-callout" style={{ marginBottom: 20 }}>
        <strong>What is a COI?</strong> A Certificate of Insurance is the document from your
        insurance company that proves you have coverage. It is sometimes called an ACORD 25.
      </div>

      <FileUpload
        label="Upload Your Certificate of Insurance (COI)"
        required
        accept=".pdf,.jpg,.jpeg,.png"
        file={data.coiFile}
        onChange={(file) => void selectCertificate(file)}
        error={errors.coiFile || analysisError}
        hint={busy ? 'Reading your certificate…' : 'Upload a PDF, photo, or scan of your COI document'}
      />

      {busy && <p role="status">Reading dates and limits from your certificate…</p>}

      {data.coiAnalysisToken && parserUnavailable && (
        <div className="info-callout" style={{ marginTop: 20 }}>
          Automatic reading is temporarily unavailable. You may continue without typing
          anything; HTS will review the uploaded certificate before dispatch is allowed.
        </div>
      )}

      {data.coiAnalysisToken && !parserUnavailable && (
        <div style={{ marginTop: 24 }}>
          <h3 style={{ color: '#0D2152' }}>Confirm what we read</h3>
          <p className="field-hint">These values came directly from your uploaded certificate.</p>

          <div className="review-grid" style={{ marginTop: 16 }}>
            {DATES.map((field) => (
              <div key={field.key} style={{ display: 'contents' }}>
                <span className="review-label">{field.label} expires</span>
                <span className="review-value">
                  {data[field.key] ? displayDate(data[field.key]) : 'Not read'}
                </span>
              </div>
            ))}
            {LIMITS.map((field) => (
              <div key={field.key} style={{ display: 'contents' }}>
                <span className="review-label">{field.label}</span>
                <span className="review-value">
                  {data.coiLimits[field.key] ? displayMoney(data.coiLimits[field.key]) : 'Not read'}
                </span>
              </div>
            ))}
          </div>

          {missingFields.length > 0 && (
            <p role="alert" className="error-text" style={{ marginTop: 16 }}>
              We could not read every required field ({missingFields.join(', ')}). Upload a
              clearer or complete certificate to continue.
            </p>
          )}
        </div>
      )}

      {data.coiAnalysisToken && (
        <label className="agreement-label" style={{ marginTop: 20 }}>
          <input
            type="checkbox"
            checked={data.coiConfirmed}
            disabled={!canConfirm}
            onChange={(event) => onChange({ coiConfirmed: event.target.checked })}
          />
          <span>
            {parserUnavailable
              ? 'I confirm this is my company’s current insurance certificate.'
              : 'I confirm the displayed dates and limits match my certificate.'}
          </span>
        </label>
      )}
      {errors.coiConfirmed && <p className="error-text">{errors.coiConfirmed}</p>}
    </div>
  );
}
